import { useCallback, useMemo } from 'react';
import { LONG_STALE_TIME, queryClient } from '../lib';
import { FilterConfigNameById, fetchFilterConfig } from '../lib/api/filterConfig';
import { WorkspaceTypeID } from '../lib/api/workspaces';
import { DTCGroupItems } from '../modules/GenomeWorkspaceView/LeftView/components/FilterConfig/DTC/DTCGroupItems';
import { DTCSubGroupItems } from '../modules/GenomeWorkspaceView/LeftView/components/FilterConfig/DTC/DTCSubGroupItems';
import type {
  FilterConfigGroupItem,
  FilterConfigSubGroupItem,
} from '../modules/GenomeWorkspaceView/LeftView/components/FilterConfig/FilterConfigTypes';
import { groupItems as defaultGroupItems } from '../modules/GenomeWorkspaceView/LeftView/components/FilterConfig/HCPExplorer/FilterConfigGroupItems';
import { subGroupItems as defaultSubGroupItems } from '../modules/GenomeWorkspaceView/LeftView/components/FilterConfig/HCPExplorer/FilterConfigSubGroupItems';
import { extendFilterConfigs } from '../modules/GenomeWorkspaceView/LeftView/components/FilterConfig/hooks/useExtendFilterConfig';
import { listMatchingGroupItems } from '../modules/GenomeWorkspaceView/LeftView/components/FilterConfig/ListMatching/ListMatchingFilterConfigGroupItems';
import { listMatchingSubGroupItems } from '../modules/GenomeWorkspaceView/LeftView/components/FilterConfig/ListMatching/ListMatchingFilterConfigSubGroupItems';
import { throtleMenuGroupList } from '../modules/GenomeWorkspaceView/LeftView/components/FilterConfig/Throtle/ThrotleFilterConfigGroupItems';
import { throtleSubGroupItems } from '../modules/GenomeWorkspaceView/LeftView/components/FilterConfig/Throtle/ThrotleFilterConfigubGroupItems';
import { useGetFilterConfigQuery } from './api/useFilterConfig';
import { useCurrentWorkspace } from './useCurrentWorkspace';

interface UseFilterConfigsParams {
  disableAutoFetch?: boolean;
  workspaceTypeId?: WorkspaceTypeID;
}

const filterGroups: Record<number, FilterConfigGroupItem[]> = {
  [WorkspaceTypeID.HCPExplorer]: defaultGroupItems,
  [WorkspaceTypeID.Throtle]: throtleMenuGroupList,
  [WorkspaceTypeID.HCPExpansion]: defaultGroupItems,
  [WorkspaceTypeID.ListMatching]: listMatchingGroupItems,
  [WorkspaceTypeID.DTC]: DTCGroupItems,
};

const filterSubGroups: Record<number, FilterConfigSubGroupItem[]> = {
  [WorkspaceTypeID.HCPExplorer]: defaultSubGroupItems,
  [WorkspaceTypeID.Throtle]: throtleSubGroupItems,
  [WorkspaceTypeID.HCPExpansion]: defaultSubGroupItems,
  [WorkspaceTypeID.ListMatching]: listMatchingSubGroupItems,
  [WorkspaceTypeID.DTC]: DTCSubGroupItems,
};

export const useFilterConfigs = (params: UseFilterConfigsParams = {}) => {
  const { disableAutoFetch = false, workspaceTypeId } = params;

  const { currentWorkspace } = useCurrentWorkspace();

  const originalWorkspaceTypeId = workspaceTypeId ?? currentWorkspace?.workspace_type_id;

  const isValidType =
    originalWorkspaceTypeId !== undefined && FilterConfigNameById[originalWorkspaceTypeId as WorkspaceTypeID] !== null;

  const effectiveWorkspaceTypeId = originalWorkspaceTypeId ?? WorkspaceTypeID.HCPExplorer;
  const {
    data: filterConfigResponse,
    isPending,
    isLoading,
    isFetching,
    isError,
    isSuccess,
    error,
    refetch,
  } = useGetFilterConfigQuery(
    { workspaceTypeId: effectiveWorkspaceTypeId },
    {
      enabled: !disableAutoFetch && isValidType,
      staleTime: LONG_STALE_TIME,
    },
  );

  const extendedFilterConfigs = useMemo(() => {
    return filterConfigResponse?.filterConfig
      ? extendFilterConfigs(filterConfigResponse.filterConfig, effectiveWorkspaceTypeId)
      : [];
  }, [filterConfigResponse, effectiveWorkspaceTypeId]);

  const getFilterConfig = useCallback(
    (filterNameOrLookmlAddress: string) => {
      return extendedFilterConfigs.find((config) => {
        return config.filterName === filterNameOrLookmlAddress || config.lookmlAddress === filterNameOrLookmlAddress;
      });
    },
    [extendedFilterConfigs],
  );

  const fetchFilterConfigs = async (workspaceTypeId: WorkspaceTypeID) => {
    const filterConfigResponse = await queryClient.fetchQuery({
      queryKey: ['filterConfig', { workspaceTypeId: workspaceTypeId ?? effectiveWorkspaceTypeId }],
      queryFn: () =>
        fetchFilterConfig({
          workspaceTypeId: workspaceTypeId ?? effectiveWorkspaceTypeId,
        }),
      staleTime: LONG_STALE_TIME,
    });

    return filterConfigResponse?.filterConfig
      ? extendFilterConfigs(filterConfigResponse.filterConfig, workspaceTypeId)
      : null;
  };

  return {
    isPending,
    isLoading,
    isFetching,
    isError,
    isSuccess,
    error,
    refetch,
    fetchFilterConfigs,
    getFilterConfig,
    filterConfigs: extendedFilterConfigs,
    filterConfigGroups: filterGroups[effectiveWorkspaceTypeId],
    filterConfigSubGroups: filterSubGroups[effectiveWorkspaceTypeId],
  };
};
